import { FabricObject, FabricImage, classRegistry, Canvas } from 'fabric';

// Импортируем кастомные классы
import { TextEntity } from '../metaBullets/classes/TextEntity';
import { Marker } from '../metaBullets/classes/Marker';

/**
 * Флаг инициализации для предотвращения повторной инициализации
 */
let isInitialized = false;

/**
 * Инициализация всех кастомизаций Fabric.js
 * Включает:
 * - Регистрацию кастомных классов
 * - Переопределение прототипов для сериализации
 * - Настройку кэширования
 * - Патчинг для CORS ошибок (isTargetTransparent)
 */
export function initializeFabric(): void {
  if (isInitialized) {
    console.debug('🎯 Fabric.js уже инициализирован, пропускаем...');
    return;
  }

  console.debug('🚀 Инициализация кастомизаций Fabric.js...');

  // === 1. РЕГИСТРАЦИЯ КАСТОМНЫХ КЛАССОВ ===
  classRegistry.setClass(TextEntity, 'textEntity');
  classRegistry.setClass(Marker, 'marker');
  console.debug('✅ Кастомные классы зарегистрированы:', {
    TextEntity: 'textEntity',
    Marker: 'marker',
  });

  // === 2. ПЕРЕОПРЕДЕЛЕНИЕ ПРОТОТИПОВ ===
  initializePrototypeOverrides();

  // === 3. НАСТРОЙКА КЭШИРОВАНИЯ ===
  FabricObject.prototype.objectCaching = true;
  FabricImage.prototype.objectCaching = true;

  // === 4. ПАТЧИНГ CORS ОШИБОК ===
  patchCanvasForCORS();

  isInitialized = true;
  console.debug('✅ Fabric.js полностью инициализирован');
}

/**
 * Патч для Canvas.prototype.isTargetTransparent
 * Перехватывает SecurityError (CORS) при вызове getImageData во время проверки прозрачности пикселя
 */
function patchCanvasForCORS(): void {
  const originalIsTargetTransparent = Canvas.prototype.isTargetTransparent;

  if (!originalIsTargetTransparent) {
    console.warn('⚠️ Canvas.prototype.isTargetTransparent не найден, CORS патч пропущен');
    return;
  }

  Canvas.prototype.isTargetTransparent = function (target: FabricObject, x: number, y: number) {
    try {
      return originalIsTargetTransparent.call(this, target, x, y);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const msg = error?.message || '';
      if (
        error?.name === 'SecurityError' ||
        msg.includes('getImageData') ||
        msg.includes('cross-origin')
      ) {
        // Подавляем CORS ошибки при проверке прозрачности
        // Возвращаем false - считаем пиксель "непрозрачным" (как безопасный фоллбэк)
        // Это предотвращает крэш приложения при наведении на картинку с внешнего домена в dev
        return false;
      }
      throw error;
    }
  };
  console.debug('✅ Fabric.js CORS патч применен для Canvas.prototype.isTargetTransparent');
}

/**
 * Переопределение метода toObject для включения кастомных свойств
 * Аналог существующего UniqueFabricObject.ts
 */
function initializePrototypeOverrides(): void {
  const originalToObject = FabricObject.prototype.toObject;

  FabricObject.prototype.toObject = function (additionalProperties: string[] = []) {
    return {
      ...originalToObject.call(this, additionalProperties),
      // Существующие кастомные поля
      id: this.id,
      name: this.name,
      layer: this.layer,
      zIndex: this.zIndex,
      category: this.category,
      idGroup: this.idGroup,
      slide_width: this.slide_width,
      slide_height: this.slide_height,
      icon_color: this.icon_color,
      source_width: this.source_width,
      source_height: this.source_height,
      backgroundColor: this.backgroundColor,
      backgroundImage: this.backgroundImage,
      noCopy: this.noCopy,
      icons: this.icons,
      shapes: this.shapes,
      isNeedScaleImgForFirstRender: this.isNeedScaleImgForFirstRender,
      available_images: this.available_images,
      shape_type: this.shape_type,
      media_type: this.media_type,
      clip_path: this.clip_path,
      source_ext: this.source_ext,
      margin: this.margin,
      space: this.space,
      inner_id: this.inner_id,
      imageColor: this.imageColor,
      missing_items: this.missing_items,
      originalOpacity: this.originalOpacity,
      isOpacityChangeFromSelection: this.isOpacityChangeFromSelection,
      isLocked: this.isLocked,
      crop: this.crop,
      originalLockState: this.originalLockState,
      alternative_text: this.alternative_text,
      is_truncated: this.is_truncated,
      extra_info: this.extra_info,
      textId: this.textId,
      isChangedContent: this.isChangedContent,
      originalText: this.originalText,
      isBulletWithoutIcon: this.isBulletWithoutIcon,
      source_type: this.source_type,

      // === НОВЫЕ ПОЛЯ МЕТАБУЛЛИТОВ ===
      entityType: this.entityType,
      metaBulletId: this.metaBulletId,
      entityOrder: this.entityOrder,
      bulletLevel: this.bulletLevel,
      isMetaBulletEntity: this.isMetaBulletEntity,
      marginBottom: this.marginBottom,
      marginLeft: this.marginLeft,
      excludeFromBackup: this.excludeFromBackup,

      // === ПОЛЯ МАРКЕРОВ ===
      isMarker: this.isMarker,
      entityId: this.entityId,
      markerId: this.markerId,
      markerContent: this.markerContent, // Персональное содержимое маркера
      markerType: this.markerType,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      content: (this as any).content, // Для объектов маркеров

      // === ПОЛЯ ДЛЯ ОБРАБОТКИ ENTER (не сериализуем функции, но сериализуем время) ===
      _lastDuplicateTime: this._lastDuplicateTime,
      // _enterKeyHandler намеренно не сериализуем (это функция)

      // Конфигурация стилей (только для TextEntity)
      ...(this.type === 'textEntity' && 'stylesConfig' in this
        ? { stylesConfig: this.stylesConfig }
        : {}),

      // === ПОЛЕ ДЛЯ PATH ===
      defined_color: this.defined_color,

      // === ПОЛЯ ДЛЯ ПЛЕЙСХОЛДЕРОВ ===
      __placeholderLabel: this.__placeholderLabel,
      isPlaceholderLabel: this.isPlaceholderLabel,
      forObjectId: this.forObjectId,

      // === ПОЛЯ ДЛЯ для картинок с category crop и image  ===
      theme_id: this.theme_id,

      // === ПОЛЯ ДЛЯ ОФФСЕТОВ КОНТЕНТА МАРКЕРА ===
      // КРИТИЧЕСКИ ВАЖНО: Эти поля должны сохраняться для корректной работы системы маркеров
      _markerOffsetX: this._markerOffsetX,
      _markerOffsetY: this._markerOffsetY,
      _markerOriginX: this._markerOriginX,
      _markerOriginY: this._markerOriginY,
    };
  };

  console.debug('✅ Переопределения прототипов применены');
}

/**
 * Проверка статуса инициализации
 */
export function isFabricInitialized(): boolean {
  return isInitialized;
}

/**
 * Принудительная переинициализация (для отладки)
 */
export function reinitializeFabric(): void {
  isInitialized = false;
  initializeFabric();
}
